import { SDUIOnClickHandler } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/SDUIOnClickHandler/SDUIOnClickHandler";
import { numberToPixel } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/conversions";
import { SDUIImageElementRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Image/SDUIImageElementRenderer";
import { SDUIRichTextRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/RichText/SDUIRichTextRenderer";
import {
  getGridSize,
  getMainImageDimensions,
  getSecondaryImageDimensions,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/imageGalleryTileLayoutUtils";
import { SDUITileInstanceRendererProps } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Tile/__internal__/types";
import { SDUITypographyRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Typography/SDUITypographyRenderer";
import { Core } from "@faire/slate/foundation/color";
import React from "react";

const AVATAR_IMAGE_SIZE = 48;

export const SDUIImageGalleryTileRenderer = ({
  tileElement,
  loggingData,
  renderBehaviour,
}: SDUITileInstanceRendererProps) => {
  const { on_click } = tileElement;
  const { image_gallery_tile } = tileElement;

  if (!image_gallery_tile) {
    return null;
  }
  const {
    header_text,
    sub_header_text,
    description_text,
    secondary_images,
    avatar_image,
    main_image,
  } = image_gallery_tile;

  if (!main_image || !avatar_image) {
    return null;
  }

  // The ImageGallery currently only supports rendering up to two secondary images
  const secondaryImagesToShow = secondary_images.slice(0, 2);

  const mainImageDimensions = getMainImageDimensions(
    secondaryImagesToShow.length
  );
  const secondaryImageDimensions = getSecondaryImageDimensions(
    secondaryImagesToShow.length
  );

  return (
    <SDUIOnClickHandler
      className="border-fs-border-muted block h-full overflow-hidden border border-solid"
      onClickActions={on_click}
      loggingData={loggingData}
      logLegacyClickEvent
    >
      <div
        className="grid h-[200px] gap-0.5"
        style={{
          gridTemplateColumns: getGridSize(secondaryImagesToShow.length)
            .columns,
          gridTemplateRows: getGridSize(secondaryImagesToShow.length).rows,
        }}
      >
        <div
          style={{
            gridRow: `1 / span ${secondaryImagesToShow.length}`,
          }}
        >
          <SDUIImageElementRenderer
            imageElement={main_image}
            loggingData={loggingData}
            imageStyles={{
              width: "100%",
              height: numberToPixel(mainImageDimensions.height),
            }}
            optimizeImageOptions={
              secondaryImagesToShow.length === 0
                ? {
                    resizeMethod: "pad",
                    ignoreResizeTag: true,
                    background: Core.surface.tertiary,
                    ...mainImageDimensions,
                  }
                : {
                    resizeMethod: "fill",
                    ...mainImageDimensions,
                  }
            }
            renderBehaviour={renderBehaviour}
          />
        </div>
        {secondaryImagesToShow.map((image, index) => {
          return (
            <SDUIImageElementRenderer
              key={index}
              imageElement={image}
              loggingData={loggingData}
              optimizeImageOptions={{
                resizeMethod: "fill",
                ...secondaryImageDimensions,
              }}
              imageStyles={{
                width: "100%",
                height: numberToPixel(secondaryImageDimensions.height),
              }}
              renderBehaviour={renderBehaviour}
            />
          );
        })}
      </div>
      <div className="flex w-full items-center gap-4 p-4">
        {/* TODO(alexieyizhe): convert image to AvatarElement */}
        <SDUIImageElementRenderer
          imageElement={avatar_image}
          loggingData={loggingData}
          renderBehaviour={renderBehaviour}
          optimizeImageOptions={{
            resizeMethod: "fill",
            width: AVATAR_IMAGE_SIZE,
            height: AVATAR_IMAGE_SIZE,
          }}
          imageStyles={{
            width: numberToPixel(AVATAR_IMAGE_SIZE),
            height: numberToPixel(AVATAR_IMAGE_SIZE),
            isCircular: true,
          }}
        />
        <div>
          {header_text ? (
            <SDUITypographyRenderer
              textElement={header_text}
              loggingData={loggingData}
            />
          ) : null}
          {sub_header_text ? (
            <SDUITypographyRenderer
              textElement={sub_header_text}
              loggingData={loggingData}
            />
          ) : null}
          {description_text ? (
            <SDUIRichTextRenderer richTextElement={description_text} />
          ) : null}
        </div>
      </div>
    </SDUIOnClickHandler>
  );
};
